import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface HomepageBanner {
  id: string;
  name: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  icon: string;
  cta_text: string | null;
  cta_link: string | null;
  badge_text: string | null;
  is_active: boolean;
  display_order: number;
  style_variant: string;
  start_date: string | null;
  end_date: string | null;
  campaign_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface HomepageBannerFormData {
  name: string;
  slug: string;
  title: string;
  subtitle?: string;
  description?: string;
  icon: string;
  cta_text?: string;
  cta_link?: string;
  badge_text?: string;
  is_active: boolean;
  display_order: number;
  style_variant: string;
  start_date?: string;
  end_date?: string;
  campaign_id?: string | null;
}

export const useHomepageBanners = () => {
  return useQuery({
    queryKey: ['homepage-banners-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('homepage_banners')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      
      // Filter by date client-side for now (RLS handles this too)
      const now = new Date();
      return (data as HomepageBanner[]).filter(banner => {
        const startValid = !banner.start_date || new Date(banner.start_date) <= now;
        const endValid = !banner.end_date || new Date(banner.end_date) > now;
        return startValid && endValid;
      });
    },
  });
};

export const useAdminHomepageBanners = () => {
  const queryClient = useQueryClient();

  const { data: banners = [], isLoading } = useQuery({
    queryKey: ['admin-homepage-banners'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('homepage_banners')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data as HomepageBanner[];
    },
  });

  const createBanner = useMutation({
    mutationFn: async (data: HomepageBannerFormData) => {
      const { error } = await supabase
        .from('homepage_banners')
        .insert([data]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-homepage-banners'] });
      queryClient.invalidateQueries({ queryKey: ['homepage-banners-active'] });
      toast.success('Banner created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create banner: ${error.message}`);
    },
  });

  const updateBanner = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<HomepageBannerFormData> }) => {
      const { error } = await supabase
        .from('homepage_banners')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-homepage-banners'] });
      queryClient.invalidateQueries({ queryKey: ['homepage-banners-active'] });
      toast.success('Banner updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update banner: ${error.message}`);
    },
  });

  const deleteBanner = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('homepage_banners')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-homepage-banners'] });
      queryClient.invalidateQueries({ queryKey: ['homepage-banners-active'] });
      toast.success('Banner deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete banner: ${error.message}`);
    },
  });

  const toggleBanner = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('homepage_banners')
        .update({ is_active })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-homepage-banners'] });
      queryClient.invalidateQueries({ queryKey: ['homepage-banners-active'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to toggle banner: ${error.message}`);
    },
  });

  return {
    banners,
    isLoading,
    createBanner,
    updateBanner,
    deleteBanner,
    toggleBanner,
  };
};
